import { createContext, useState, lazy, Suspense, useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { isLoggedIn, isNewlyRegistered } from "./apis/util.js";
import "./index.css";
import "./globalicons.css";
import "./carousel.css";
import "./pac.css";
import "./inputs.css";
import "./emission.css";
import "react-multi-carousel-18/lib/styles.css";
import Spinners from "./Spinners.js";
import { ToastProvider } from "./components/Toast/ToastContext.jsx";

import i18n from "./i18n";
import LocaleContext from "./LocaleContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import AboutYFS from "./app/AboutYFS";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "react-bootstrap";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useEffect } from "react";
import AboutYFSPublic from "./app/AboutYFS/AboutYFSPublic.js";
import { UserAccessProvider } from "./components/WriteAccessContext/index.jsx";
import { InvalidateQueryProvider } from "./components/InvalidateQueryContext/index.jsx";
import { UserModulesProvider } from "./components/UserModuleContext/index.jsx";
import DanishNumberProvider from "./components/DanishNumberProvider";
import {
  CompanyRoutes,
  ProductRoutes,
  AmbitionRoutes,
  ReportingRoutes,
  // MapRoutes,
  FeedRoutes,
  EducationRoutes,
  AdminRoutes,
  BusinessRoutes,
  ClientRoutes,
  ShareRoutes,
} from "./ModuleRouter";
import ProtectedRoute from "./components/UserModuleContext/ProtectedRoute.jsx";

const Profile = lazy(() => import("./app/NewProfile"));
const Output = lazy(() => import("./app/Reporting/Output"));
const Skeleton = lazy(() => import("./components/Skeleton"));
const Canvas = lazy(() => import("./app/Canvas"));
const ForgotPassword = lazy(() => import("./app/ForgotPassword"));
const ForgotPasswordForm = lazy(() => import("./app/ForgotPasswordForm"));
const WasteMap = lazy(() => import("./app/WasteMap"));
const Overview = lazy(() => import("./app/Overview"));
const YFSLabel = lazy(() => import("./app/YFSLabel/index.jsx"));
const VerifyBackupEmail = lazy(() =>
  import("./components/VerifyBackupEmail/index.jsx")
);
const Navigator = lazy(() => import("./components/Navigator/index.jsx"));
const LandingPage = lazy(() => import("./app/LandingPage/index.jsx"));
const SubscriptionSuccess = lazy(() =>
  import("./app/LandingPage/Registration/SubscriptionSuccess.jsx")
);
const StaffDashboard = lazy(() =>
  import("./app/SolutionsLab/StaffDashboard/StaffDashboard.jsx")
);
const PrivacyPolicyPage = lazy(() =>
  import("./app/LandingPage/PrivacyPolicyPage/index.jsx")
);

const TermsOfUse = lazy(() =>
  import("./app/LandingPage/TermsOfUsePage/index.jsx")
);

const BugReport = lazy(() => import("./components/BugReport/BugReport"));

export const AccountContext = createContext();
export const NavContext = createContext();
export const ContainerBody = ({ children, className, ref, style }) => {
  return (
    <div
      className={"d-flex flex-column w-100 " + className}
      ref={ref}
      style={{
        width: "100%",
        height: "100%",
        paddingLeft: "30px",
        paddingBottom: "30px",
        ...style,
      }}
    >
      {children}
    </div>
  );
};
export const RegisterContext = createContext();

function AppRouter() {
  const [nav, setNav] = useState(1);

  const [locale, setLocale] = useState(i18n.language);

  i18n.on("languageChanged", (lng) => setLocale(i18n.language));
  const { t } = useTranslation();
  const [newAccount, setNewAccount] = useState(true);
  const [move, setMove] = useState(false);
  const handleClosePopup = () => {
    setNewAccount(false);
    localStorage.removeItem("newly_registered_az");
  };

  useEffect(() => {}, [newAccount]);
  const [register, setRegister] = useState(false);
  if (isLoggedIn()) {
    return (
      <div className="App">
        {newAccount && isNewlyRegistered() ? (
          <Modal
            size="lg"
            show={true}
            centered
            onHide={() => handleClosePopup()}
          >
            <ModalBody className="d-flex justify-content-center align-items-center">
              <div className="company__popup__registered text-center">
                <h5>{t("REGISTER_POPUP_1")}</h5>
                <br />
                <h5>{t("REGISTER_POPUP_2")}</h5>
                <br />
                <h5>{t("REGISTER_POPUP_3")}</h5>
                <br />
                <h5>{t("REGISTER_POPUP_4")}</h5>
                <br />
                <h5>{t("REGISTER_POPUP_5")}</h5>
                <br />
                <div className="popup__icon">
                  <CancelOutlinedIcon onClick={() => handleClosePopup()} />
                </div>
              </div>
            </ModalBody>
          </Modal>
        ) : null}

        <LanguageProvider>
          <LocaleContext.Provider value={{ locale, setLocale }}>
            <NavContext.Provider value={[nav, setNav]}>
              <Skeleton
                content={
                  <Suspense fallback={<Spinners />}>
                    <Routes>
                      <Route
                        path="/company/*"
                        element={
                          <ProtectedRoute
                            component={CompanyRoutes}
                            requiredModule="Company"
                          />
                        }
                      />
                      <Route
                        path="/product/*"
                        element={
                          <ProtectedRoute
                            component={ProductRoutes}
                            requiredModule="Product"
                          />
                        }
                      />
                      <Route
                        path="/ambition/*"
                        element={
                          <ProtectedRoute
                            component={AmbitionRoutes}
                            requiredModule="Ambition"
                          />
                        }
                      />
                      <Route
                        path="/reporting/*"
                        element={
                          <ProtectedRoute
                            component={ReportingRoutes}
                            requiredModule="Report"
                          />
                        }
                      />
                      <Route
                        path="/feed/*"
                        element={
                          <ProtectedRoute
                            component={FeedRoutes}
                            requiredModule="Feed"
                          />
                        }
                      />
                      <Route
                        path="/education/*"
                        element={
                          <ProtectedRoute
                            component={EducationRoutes}
                            requiredModule="Education"
                          />
                        }
                      />
                      <Route
                        path="/admin-dash/*"
                        element={
                          <ProtectedRoute
                            component={AdminRoutes}
                            requiredModule="Admin"
                          />
                        }
                      />
                      <Route
                        path="/business-dash/*"
                        element={
                          <ProtectedRoute
                            component={BusinessRoutes}
                            requiredModule="Business"
                          />
                        }
                      />
                      <Route
                        path="/client/*"
                        element={
                          <ProtectedRoute
                            component={ClientRoutes}
                            requiredModule="Client"
                          />
                        }
                      />
                      <Route
                        path="/share/*"
                        element={
                          <ProtectedRoute
                            component={ShareRoutes}
                            requiredModule="Share"
                          />
                        }
                      />

                      <Route
                        path="/verify-backup-email/:token"
                        element={<VerifyBackupEmail />}
                      />
                      <Route path="/info-navigation" element={<Navigator />} />
                      <Route exact path="/waste-map" element={<WasteMap />} />
                      <Route exact path="/about" element={<AboutYFS />} />

                      <Route exact path="/profile" element={<Profile />} />

                      <Route exact path="/output" element={<Output />} />
                      <Route exact path="/canvas" element={<Canvas />} />
                      <Route exact path="/overview" element={<Overview />} />
                      <Route exact path="/ar-dash" element={<YFSLabel />} />
                      <Route
                        exact
                        path="/staff-dashboard"
                        element={<StaffDashboard />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/info-navigation" />}
                      />
                    </Routes>
                    <BugReport />
                  </Suspense>
                }
              />
            </NavContext.Provider>
          </LocaleContext.Provider>
        </LanguageProvider>
      </div>
    );
  } else {
    return (
      <div className="App">
        <LanguageProvider>
          <RegisterContext.Provider value={[newAccount, setNewAccount]}>
            <Suspense fallback={<Spinners />}>
              <Routes>
                <Route
                  path="/subscription-success"
                  element={<SubscriptionSuccess />}
                />
                {/* <Route
                exact
                path="/register"
                element={<Register setMove={setMove} />}
              /> */}
                <Route exact path="/term-of-use" element={<TermsOfUse />} />
                <Route
                  exact
                  path="/privacy-policy"
                  element={<PrivacyPolicyPage />}
                />
                <Route
                  path="*"
                  element={
                    <LandingPage setRegister={setRegister} setMove={setMove} />
                  }
                />
                {/* <Route
                path="/login"
                element={<Home setRegister={setRegister} setMove={setMove} />}
              /> */}
                <Route exact path="/about" element={<AboutYFSPublic />} />
                <Route
                  exact
                  path="/forgot-password"
                  element={<ForgotPassword />}
                />
                <Route
                  exact
                  path="/password/reset/verify/"
                  element={<ForgotPasswordForm />}
                />
              </Routes>
              <BugReport />
            </Suspense>
          </RegisterContext.Provider>
        </LanguageProvider>
      </div>
    );
  }
}

function App() {
  const [account, setAccount] = useState();

  return (
    <AccountContext.Provider value={{ account, setAccount }}>
      <InvalidateQueryProvider>
        <UserAccessProvider>
          <ToastProvider>
            <DanishNumberProvider>
              {isLoggedIn() ? (
                <UserModulesProvider>
                  <AppRouter />
                </UserModulesProvider>
              ) : (
                <AppRouter />
              )}
            </DanishNumberProvider>
          </ToastProvider>
        </UserAccessProvider>
      </InvalidateQueryProvider>
    </AccountContext.Provider>
  );
}

export default App;
